import { apiClient } from './client';
import { API_CONFIG } from './config';
import type { 
  Institution, 
  InstitutionsListResponse, 
  InstitutionResponse,
  GetInstitutionsParams,
  InstitutionBranding
} from '../../types/institution';

// Institution Service Class
export class InstitutionService {
  /**
   * Transform API response to ensure proper typing and handle any missing fields
   */
  private transformInstitution(institution: any): Institution {
    return {
      // Required fields
      id: institution.id,
      name: institution.name,
      
      // Optional URL fields
      home_url: institution.home_url,
      login_url: institution.login_url,
      forgot_password_url: institution.forgot_password_url,
      signup_url: institution.signup_url,
      
      // Feature support flags
      supports_transaction_aggregation: institution.supports_transaction_aggregation,
      supports_account_verification: institution.supports_account_verification,
      supports_account_history: institution.supports_account_history,
      supports_account_owner_info: institution.supports_account_owner_info,
      supports_student_loan_data: institution.supports_student_loan_data,
      supports_loan_payment_details: institution.supports_loan_payment_details,
      
      // Classification fields
      account_type: institution.account_type,
      institution_type: institution.institution_type,
      status: institution.status,
      time_zone: institution.time_zone,
      currency: institution.currency,
      
      // Contact information
      phone: institution.phone,
      email: institution.email,
      
      // Address and branding objects (these are the key fields for UI)
      address: institution.address,
      branding: institution.branding,
      
      // OAuth support
      oauth_enabled: institution.oauth_enabled,
      
      // Special instructions
      special_instructions: institution.special_instructions,
      special_text: institution.special_text,
    };
  }

  /**
   * Get all institutions
   * @param params - Query parameters for filtering institutions
   * @returns Promise with institutions data
   */
  public async getInstitutions(params?: GetInstitutionsParams): Promise<Institution[]> {
    try {
      // Build query string from parameters
      const queryParams = new URLSearchParams();
      
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.start) {
        queryParams.append('start', params.start.toString());
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }

      // Always add type=ach parameter
      queryParams.append('type', 'ach');

      const queryString = queryParams.toString();
      const endpoint = `${API_CONFIG.ENDPOINTS.INSTITUTIONS}${queryString ? `?${queryString}` : ''}`;

      const response = await apiClient.get<InstitutionsListResponse>(endpoint);
      
      // Handle the new API response format: { institutions: [...], total_count: number }
      if (response.data && Array.isArray(response.data.institutions)) {
        return response.data.institutions.map(inst => this.transformInstitution(inst));
      } else {
        console.warn('Unexpected institutions response format:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Failed to fetch institutions:', error);
      throw error;
    }
  }

  /**
   * Get a specific institution by ID
   * @param institutionId - The institution ID
   * @returns Promise with institution data
   */
  public async getInstitutionById(institutionId: string): Promise<Institution> {
    try {
      const endpoint = `${API_CONFIG.ENDPOINTS.INSTITUTIONS}${institutionId}`;
      const response = await apiClient.get<InstitutionResponse>(endpoint);
      return this.transformInstitution(response.data);
    } catch (error) {
      console.error(`Failed to fetch institution ${institutionId}:`, error);
      throw error;
    }
  }

  /**
   * Search institutions by name
   * @param searchTerm - The search term
   * @param limit - Maximum number of results
   * @returns Promise with matching institutions
   */
  public async searchInstitutions(searchTerm: string, limit: number = 50): Promise<Institution[]> {
    return this.getInstitutions({
      search: searchTerm,
      limit,
    });
  }

  /**
   * Get institutions with OAuth support
   * @param limit - Maximum number of results
   * @returns Promise with OAuth-enabled institutions
   */
  public async getOAuthInstitutions(limit: number = 100): Promise<Institution[]> {
    const institutions = await this.getInstitutions({ limit });
    return institutions.filter(institution => institution.oauth_enabled);
  }

  /**
   * Get popular/featured institutions
   * This would typically be based on usage statistics or manual curation
   * @param limit - Maximum number of results
   * @returns Promise with popular institutions
   */
  public async getPopularInstitutions(limit: number = 20): Promise<Institution[]> {
    // For now, just return the first N institutions
    // In a real implementation, this would have special logic
    return this.getInstitutions({ limit });
  }

  /**
   * Get institution branding information by ID
   * @param institutionId - The institution ID
   * @returns Promise with institution branding data
   */
  public async getInstitutionBranding(institutionId: string): Promise<InstitutionBranding> {
    try {
      const endpoint = `${API_CONFIG.ENDPOINTS.INSTITUTIONS}${institutionId}/branding`;
      const response = await apiClient.get<InstitutionBranding>(endpoint);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch institution branding:', error);
      throw error;
    }
  }

  /**
   * Sync institutions from external source
   * @param params - Query parameters for syncing institutions
   * @returns Promise indicating sync completion
   */
  public async syncInstitutions(params?: GetInstitutionsParams): Promise<void> {
    try {
      // Build query string from parameters
      const queryParams = new URLSearchParams();
      
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.start) {
        queryParams.append('start', params.start.toString());
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }

      const queryString = queryParams.toString();
      const endpoint = `${API_CONFIG.ENDPOINTS.INSTITUTIONS}sync${queryString ? `?${queryString}` : ''}`;

      await apiClient.post(endpoint);
    } catch (error) {
      console.error('Failed to sync institutions:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const institutionService = new InstitutionService(); 